"use client";

import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { MoreHorizontal, Eye, Edit, Trash2 } from "lucide-react";
import { usePermissions } from "../hooks/usePermissions";
import styles from "./ActionMenu.module.css";

interface ActionMenuItem {
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  variant?: 'default' | 'danger';
  requiredPermission?: string | string[]; // Permission code cần thiết để hiển thị item này
}

interface ActionMenuProps {
  items: ActionMenuItem[];
  className?: string;
}

export default function ActionMenu({ items, className = "" }: ActionMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const { hasPermission } = usePermissions();

  // Filter items dựa trên permissions
  const visibleItems = items.filter(item => {
    if (!item.requiredPermission) return true; // Không yêu cầu permission
    return hasPermission(item.requiredPermission);
  });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        (menuRef.current && menuRef.current.contains(target)) ||
        (wrapperRef.current && wrapperRef.current.contains(target))
      ) return;
      setIsOpen(false);
    };

    const updatePosition = () => {
      if (!wrapperRef.current) return;
      const rect = wrapperRef.current.getBoundingClientRect();
      const estimatedWidth = 160;
      const left = Math.max(8, Math.min(rect.right - estimatedWidth, window.innerWidth - estimatedWidth - 8));
      const top = rect.bottom + 6;
      setCoords({ top, left });
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
      updatePosition();
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen]);

  const handleItemClick = (onClick: () => void) => {
    onClick();
    setIsOpen(false);
  };

  // Nếu không có item nào hiển thị, không render menu (sau khi đã gọi tất cả hooks)
  if (visibleItems.length === 0) {
    return null;
  }

  return (
    <>
      <div className={`${styles.actionMenu} ${className}`} ref={wrapperRef}>
        <button 
          className={styles.trigger}
          onClick={() => setIsOpen((v) => !v)}
          aria-label="Hành động"
        >
          <MoreHorizontal size={16} />
        </button>
      </div>

      {isOpen && coords && typeof window !== 'undefined' && createPortal(
        <div
          ref={menuRef}
          className={styles.menu}
          style={{ position: 'fixed', top: coords.top, left: coords.left, zIndex: 9999 }}
        >
          {visibleItems.map((item, index) => (
            <button
              key={index}
              className={`${styles.menuItem} ${item.variant === 'danger' ? styles.danger : ''}`}
              onClick={() => handleItemClick(item.onClick)}
            >
              {item.icon}
              {item.label}
            </button>
          ))}
        </div>,
        document.body
      )}
    </>
  );
}

// Predefined action items for common use cases
export const ActionMenuItems = {
  view: (onClick: () => void, requiredPermission?: string | string[]): ActionMenuItem => ({
    label: "Xem chi tiết",
    icon: <Eye size={16} />,
    onClick,
    requiredPermission
  }),
  
  edit: (onClick: () => void, requiredPermission?: string | string[]): ActionMenuItem => ({
    label: "Chỉnh sửa", 
    icon: <Edit size={16} />,
    onClick,
    requiredPermission
  }),
  
  delete: (onClick: () => void, requiredPermission?: string | string[]): ActionMenuItem => ({
    label: "Xóa",
    icon: <Trash2 size={16} />,
    onClick,
    variant: 'danger',
    requiredPermission
  })
};
